import React, { useState } from 'react'
import { Database, ChevronRight } from 'lucide-react'
import OntologyTag from '../components/OntologyTag'

const OWL_CLASSES = [
  { name: 'LinearFunction', label_ko: '1차 함수', comment: '일반형 y=ax+b (a≠0)인 함수', parent: null },
  { name: 'ProportionalFunction', label_ko: '정비례 함수', comment: 'y=ax (b=0)인 1차 함수의 특별한 경우', parent: 'LinearFunction' },
  { name: 'Variable', label_ko: '변수', comment: '변하는 값을 나타내는 기호', parent: null },
  { name: 'IndependentVariable', label_ko: '독립변수', comment: '독립적으로 변하는 변수 (x)', parent: 'Variable' },
  { name: 'DependentVariable', label_ko: '종속변수', comment: '독립변수에 의해 결정되는 변수 (y)', parent: 'Variable' },
  { name: 'Slope', label_ko: '기울기', comment: 'x 1 증가에 대한 y의 변화량 (a)', parent: null },
  { name: 'YIntercept', label_ko: 'y절편', comment: '그래프가 y축과 만나는 점의 y좌표 (b)', parent: null },
  { name: 'Graph', label_ko: '그래프', comment: '함수를 좌표평면에 나타낸 직선', parent: null },
  { name: 'IncreasingGraph', label_ko: '증가 그래프', comment: '기울기 > 0인 우상향 그래프', parent: 'Graph' },
  { name: 'DecreasingGraph', label_ko: '감소 그래프', comment: '기울기 < 0인 우하향 그래프', parent: 'Graph' },
  { name: 'CoordinatePlane', label_ko: '좌표평면', comment: 'x축과 y축으로 이루어진 평면', parent: null },
  { name: 'Point', label_ko: '점', comment: '좌표평면 위의 위치', parent: null },
  { name: 'OrderedPair', label_ko: '순서쌍', comment: '(x, y) 형태의 쌍', parent: 'Point' },
  { name: 'Equation', label_ko: '방정식', comment: '1차 함수를 나타내는 식 (y=ax+b)', parent: null },
  { name: 'Table', label_ko: '표', comment: 'x와 y의 대응 관계를 나타내는 표', parent: null },
  { name: 'ChangeRate', label_ko: '변화율', comment: 'Δy/Δx', parent: null },
  { name: 'ProportionalRelationship', label_ko: '정비례 관계', comment: 'y/x가 일정한 관계', parent: null },
  { name: 'WordProblem', label_ko: '문장제', comment: '실생활 맥락의 문제', parent: null },
  { name: 'RealWorldSituation', label_ko: '실생활 상황', comment: '실제 맥락의 상황', parent: null },
  { name: 'StudentAnswer', label_ko: '학생 답안', comment: '학생이 제출한 답', parent: null },
  { name: 'Misconception', label_ko: '오개념', comment: '잘못된 개념 이해', parent: null },
  { name: 'Feedback', label_ko: '피드백', comment: 'AI 튜터가 제공하는 교정 피드백', parent: null },
  { name: 'LearningStep', label_ko: '학습 단계', comment: '12단계 학습 경로의 각 단계', parent: null },
]

const OBJECT_PROPS = [
  { name: 'hasEquation', domain: 'LinearFunction', range: 'Equation', label_ko: '방정식을 가진다' },
  { name: 'hasSlope', domain: 'LinearFunction', range: 'Slope', label_ko: '기울기를 가진다' },
  { name: 'hasYIntercept', domain: 'LinearFunction', range: 'YIntercept', label_ko: 'y절편을 가진다' },
  { name: 'isRepresentedBy', domain: 'LinearFunction', range: 'Graph', label_ko: '그래프로 표현된다' },
  { name: 'hasPoint', domain: 'Graph', range: 'Point', label_ko: '점을 가진다' },
  { name: 'hasXValue', domain: 'Point', range: 'IndependentVariable', label_ko: 'x값을 가진다' },
  { name: 'hasYValue', domain: 'Point', range: 'DependentVariable', label_ko: 'y값을 가진다' },
  { name: 'triggers', domain: 'StudentAnswer', range: 'Misconception', label_ko: '오개념을 유발한다' },
  { name: 'recommends', domain: 'Misconception', range: 'Feedback', label_ko: '피드백을 권장한다' },
  { name: 'teaches', domain: 'LearningStep', range: 'LinearFunction', label_ko: '가르친다' },
  { name: 'canBeModeledBy', domain: 'RealWorldSituation', range: 'LinearFunction', label_ko: '모델링된다' },
  { name: 'describes', domain: 'WordProblem', range: 'RealWorldSituation', label_ko: '설명한다' },
]

const DATA_PROPS = [
  { name: 'slopeValue', domain: 'Slope', range: 'xsd:decimal', label_ko: '기울기 값' },
  { name: 'interceptValue', domain: 'YIntercept', range: 'xsd:decimal', label_ko: 'y절편 값' },
  { name: 'xValue', domain: 'Point', range: 'xsd:decimal', label_ko: 'x 좌표값' },
  { name: 'yValue', domain: 'Point', range: 'xsd:decimal', label_ko: 'y 좌표값' },
  { name: 'equationText', domain: 'Equation', range: 'xsd:string', label_ko: '방정식 텍스트' },
  { name: 'problemText', domain: 'WordProblem', range: 'xsd:string', label_ko: '문제 텍스트' },
  { name: 'answerText', domain: 'StudentAnswer', range: 'xsd:string', label_ko: '답안 텍스트' },
  { name: 'feedbackText', domain: 'Feedback', range: 'xsd:string', label_ko: '피드백 텍스트' },
  { name: 'difficultyLevel', domain: 'LearningStep', range: 'xsd:string', label_ko: '난이도' },
  { name: 'learningObjective', domain: 'LearningStep', range: 'xsd:string', label_ko: '학습 목표' },
  { name: 'stepOrder', domain: 'LearningStep', range: 'xsd:integer', label_ko: '단계 순서' },
]

const SHACL = [
  { name: 'LinearFunctionShape', target: 'LinearFunction', constraints: ['hasEquation 최소 1개', 'hasSlope 최소 1개', 'hasYIntercept 최소 1개'] },
  { name: 'PointShape', target: 'Point', constraints: ['xValue 정확히 1개 (decimal)', 'yValue 정확히 1개 (decimal)'] },
  { name: 'StudentAnswerShape', target: 'StudentAnswer', constraints: ['answerText 정확히 1개 (string)'] },
  { name: 'FeedbackShape', target: 'Feedback', constraints: ['feedbackText 최소 1개 (string)', 'feedbackText 최대 1개'] },
  { name: 'LearningStepShape', target: 'LearningStep', constraints: ['learningObjective 정확히 1개', 'stepOrder 정확히 1개 (integer)'] },
  { name: 'MisconceptionShape', target: 'Misconception', constraints: ['recommends Feedback 최소 1개'] },
]

type Tab = 'classes' | 'object' | 'data' | 'shacl'

export default function OntologyPage() {
  const [tab, setTab] = useState<Tab>('classes')
  const [search, setSearch] = useState('')

  const tabs: { id: Tab; label: string }[] = [
    { id: 'classes', label: `OWL 클래스 (${OWL_CLASSES.length})` },
    { id: 'object', label: `객체 속성 (${OBJECT_PROPS.length})` },
    { id: 'data', label: `데이터 속성 (${DATA_PROPS.length})` },
    { id: 'shacl', label: `SHACL 제약 (${SHACL.length})` },
  ]

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center gap-3">
        <Database size={22} className="text-orange-600" />
        <div>
          <h1 className="text-xl font-bold text-slate-800">온톨로지 브라우저</h1>
          <p className="text-sm text-slate-500">OWL/RDF 클래스, 속성, SHACL 제약 조회</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: 'OWL 클래스', value: OWL_CLASSES.length, color: 'bg-purple-100 text-purple-700' },
          { label: '객체 속성', value: OBJECT_PROPS.length, color: 'bg-blue-100 text-blue-700' },
          { label: '데이터 속성', value: DATA_PROPS.length, color: 'bg-emerald-100 text-emerald-700' },
          { label: 'SHACL Shape', value: SHACL.length, color: 'bg-orange-100 text-orange-700' },
        ].map(s => (
          <div key={s.label} className="card text-center">
            <div className={`text-2xl font-bold ${s.color.split(' ')[1]}`}>{s.value}</div>
            <div className="text-xs text-slate-500 mt-0.5">{s.label}</div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-slate-100 rounded-xl p-1">
        {tabs.map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`flex-1 text-xs py-2 px-2 rounded-lg font-medium transition-all ${tab === t.id ? 'bg-white text-primary-700 shadow-sm' : 'text-slate-600 hover:text-slate-800'}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Search */}
      <input
        type="text"
        placeholder="검색..."
        value={search}
        onChange={e => setSearch(e.target.value)}
        className="w-full text-sm border border-slate-200 rounded-xl px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-primary-300"
      />

      {/* Classes */}
      {tab === 'classes' && (
        <div className="space-y-2">
          {OWL_CLASSES.filter(c => c.name.toLowerCase().includes(search.toLowerCase()) || c.label_ko.includes(search)).map(cls => (
            <div key={cls.name} className="card border border-slate-200">
              <div className="flex items-start gap-3">
                <OntologyTag className_name={cls.name} />
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-slate-800">{cls.label_ko}</span>
                    {cls.parent && (
                      <div className="flex items-center gap-1 text-xs text-slate-400">
                        <ChevronRight size={12} />
                        <OntologyTag className_name={cls.parent} variant="blue" />
                        <span>의 하위 클래스</span>
                      </div>
                    )}
                  </div>
                  <p className="text-xs text-slate-500 mt-0.5">{cls.comment}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Object Properties */}
      {tab === 'object' && (
        <div className="space-y-2">
          {OBJECT_PROPS.filter(p => p.name.toLowerCase().includes(search.toLowerCase()) || p.label_ko.includes(search)).map(prop => (
            <div key={prop.name} className="card border border-slate-200">
              <div className="flex items-center gap-3 flex-wrap">
                <OntologyTag className_name={prop.name} variant="blue" />
                <span className="text-sm text-slate-700">{prop.label_ko}</span>
                <div className="ml-auto flex items-center gap-2 text-xs text-slate-500">
                  <OntologyTag className_name={prop.domain} size="sm" />
                  <ChevronRight size={12} />
                  <OntologyTag className_name={prop.range} size="sm" variant="green" />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Data Properties */}
      {tab === 'data' && (
        <div className="space-y-2">
          {DATA_PROPS.filter(p => p.name.toLowerCase().includes(search.toLowerCase()) || p.label_ko.includes(search)).map(prop => (
            <div key={prop.name} className="card border border-slate-200">
              <div className="flex items-center gap-3 flex-wrap">
                <OntologyTag className_name={prop.name} variant="green" />
                <span className="text-sm text-slate-700">{prop.label_ko}</span>
                <div className="ml-auto flex items-center gap-2 text-xs text-slate-500">
                  <OntologyTag className_name={prop.domain} size="sm" />
                  <ChevronRight size={12} />
                  <span className="font-mono text-xs bg-slate-100 px-1.5 py-0.5 rounded text-slate-600">{prop.range}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* SHACL */}
      {tab === 'shacl' && (
        <div className="space-y-3">
          {SHACL.filter(s => s.name.toLowerCase().includes(search.toLowerCase()) || s.target.toLowerCase().includes(search.toLowerCase())).map(shape => (
            <div key={shape.name} className="card border border-orange-200 bg-orange-50">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-xs font-mono font-bold text-orange-700 bg-orange-100 px-2 py-0.5 rounded">{shape.name}</span>
                <ChevronRight size={12} className="text-orange-400" />
                <OntologyTag className_name={shape.target} variant="purple" />
              </div>
              <div className="space-y-1">
                {shape.constraints.map((c, i) => (
                  <div key={i} className="text-xs text-orange-800 flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-orange-400 flex-shrink-0" />
                    {c}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
